import {
  GDSTypographyStyle,
  type GDSTypography,
} from '@leagueplatform/genesis-core';
import { PartialDeep } from 'type-fest';

/**
 * `headingOne`, `bodyTwo`, etc.
 */
type Token = keyof GDSTypography;

const STYLE_PROPERTY_TO_SCALE = {
  fontSize: 'fontSizes',
  fontWeight: 'fontWeights',
  fontFamily: 'fonts',
  lineHeight: 'lineHeights',
  letterSpacing: 'letterSpacings',
  textDecoration: 'textDecorations',
  textCase: 'textTransforms',
} as const;

const SCALES = Object.values(STYLE_PROPERTY_TO_SCALE);

/**
 * `fontSize`, `lineHeight`, etc.
 */
type StyleProperty = keyof typeof STYLE_PROPERTY_TO_SCALE;

/**
 * Stitches typography-related scale names.
 */
type ThemeScaleProperty = (typeof STYLE_PROPERTY_TO_SCALE)[StyleProperty];

type StylePropertyFromThemeScaleProperty<T extends ThemeScaleProperty> = {
  [Key in StyleProperty]: (typeof STYLE_PROPERTY_TO_SCALE)[Key] extends T
    ? Key
    : never;
}[StyleProperty];

/**
 * The shape in which Masonry nodes may specify typography theme token overrides.
 * E.g.
 * {
 *   // token-to-token override
 *   headingOne: '$bodyTwo',
 *
 *   // token-to-explicit style properties override
 *   subtitleThree: {
 *     fontSize: 18,
 *     lineHeight: 24,
 *   },
 * }
 */
type TypographyTokenOverrides = PartialDeep<{
  [key in Token]: `$${Token}` | GDSTypographyStyle;
}>;

/**
 * `TypographyTokenOverrides` converted to regular Stitches scales.
 * E.g.
 * {
 *   fontSizes: {
 *     headingOne: '$bodyTwo',
 *     subtitleThree: 18,
 *   },
 *   lineHeights: {
 *     subtitleThree: 24,
 *   },
 * }
 */
type StitchifiedTypography = PartialDeep<{
  [scaleProperty in ThemeScaleProperty]: PartialDeep<{
    [key in Token]: GDSTypographyStyle[StylePropertyFromThemeScaleProperty<scaleProperty>];
  }>;
}>;

type ToStitchesTypographyScalesOptions = {
  baseSize: number;
  useRems: boolean;
};

const NUMERIC_PROPERTIES: ReadonlySet<StyleProperty> = new Set([
  'fontSize',
  'lineHeight',
  'letterSpacing',
]);

const convertLengthValue = (
  value: number | string,
  options: ToStitchesTypographyScalesOptions,
) => {
  if (typeof value !== 'number') {
    const numericValue = Number(value);
    if (Number.isNaN(numericValue)) {
      return value;
    }
    // eslint-disable-next-line no-param-reassign
    value = numericValue;
  }

  if (!options.useRems) {
    return value === 0 ? value : `${value}px`;
  }

  return `${value / (options.baseSize || 16)}rem`;
};

export const toStitchesTypographyScales = (
  typographyOverrides: TypographyTokenOverrides,
  options: ToStitchesTypographyScalesOptions,
): StitchifiedTypography => {
  const stitchesTypographyScales: StitchifiedTypography = {};

  Object.entries(typographyOverrides ?? {}).forEach(([token, override]) => {
    if (typeof override === 'string') {
      SCALES.forEach((scaleProperty) => {
        const scale = (stitchesTypographyScales[scaleProperty] ??=
          {}) as StitchifiedTypography[ThemeScaleProperty];
        (scale as Record<string, string>)[token] = override;
      });

      return;
    }

    Object.entries(override).forEach(([styleProperty, value]) => {
      const scaleProperty =
        STYLE_PROPERTY_TO_SCALE[styleProperty as StyleProperty];
      const scale = (stitchesTypographyScales[scaleProperty] ??=
        {}) as StitchifiedTypography[ThemeScaleProperty];

      const stitchedValue: unknown =
        NUMERIC_PROPERTIES.has(styleProperty as StyleProperty) &&
        (typeof value === 'number' || typeof value === 'string')
          ? convertLengthValue(value, options)
          : value;

      (scale as Record<string, unknown>)[token] = stitchedValue;
    });
  });

  return stitchesTypographyScales;
};
